<template>
  <div v-if="bandLyric" :class="contentClass" class="content">
    <div class="header-wrapper" :class="header">
      <div :class="pagination">
        <SmallButton v-if="this.bandLyric.previousLyric" class="button-change-page" @click="handleLyricChangePage(-1)">
          Poprzednia
        </SmallButton>
        <p v-if="this.bandLyric.previousLyric" class="font-size-small">{{ this.bandLyric.previousLyric.title }}</p>
      </div>
      <header v-if="!isAndroidContext">
        <h1 class="font-size-big">{{ pageTitle }}</h1>
      </header>
      <div class="next" :class="pagination">
        <SmallButton v-if="this.bandLyric.nextLyric && !loadingNewPage"
                     class="button-change-page" @click="handleLyricChangePage(1)">
          Następna
        </SmallButton>
        <SmallButton v-if="this.bandLyric.nextLyric && loadingNewPage"
                     class="button-change-page">
          Następna
        </SmallButton>
        <p v-if="this.bandLyric.nextLyric" class="font-size-small">{{ this.bandLyric.nextLyric.title }}</p>
      </div>
    </div>
    <section class="actions">
      <SmallButton v-if="isEditingMode" style="border-color: red"
                   @click="confirmLyricDelete">Usuń
      </SmallButton>
      <SmallButton @click="cancelBandLyricForm">Anuluj</SmallButton>
      <CircleLoadingButton :isLoading="isLoading && saving" marginLeft="20">
        <SmallButton
            border-color="primary"
            type="submit"
            :smaller="true"
            @click.native="handleSaveClicked"
        >Zapisz
        </SmallButton>
      </CircleLoadingButton>
    </section>
    <div class="own-lyric-form">
      <section :class="{error: animate}"
               class="input-fields">
        <div v-if="loadingError" class="lyricError">
          <div>
            <img
                :class="{ bounce: animate }"
                alt=""
                src="@/assets/images/errorIcon.svg"
            />
            <p class="font-size-middle">Tytuł oraz czas trwania piosenki są wymagane.</p>
          </div>
        </div>
        <div class="upper-inputs">
          <DefaultInputField
              :value="bandLyric.title"
              aria-required="true"
              placeholder="Tytuł piosenki"
              @input="handleTitleChange"
          />
          <div class="text-input">
            <input
                class="time-input"
                required="true"
                type="text"
                v-model="time"
                @input="handleTimeChange"
                placeholder="Czas"
                v-cleave="{time: true, timePattern: ['m', 's']}"
            >
            <label class="font-size-middle">Czas</label>
          </div>
        </div>
        <div class="tags-wrapper" :class="tagsBar">
          <VoerroTagsInput element-id='tags' v-model='selectedTags'
            @focus="handleBarColor" 
            @blur="handleBarColor"
            :existing-tags="teamTags"
            :typeahead="true"
            :typeahead-hide-discard="true"
            :typeahead-activation-threshold="0"
            placeholder="Tagi piosenki">
            <template v-slot:selected-tag="{ tag, index, removeTag }">
              <span v-html="tag.value"></span>
              <a
                href="#"
                class="tags-input-remove"
                @click.prevent="removeTag(index)">
                  <font-awesome-icon icon="times" />
              </a>
            </template>
          </VoerroTagsInput>
        </div>
        <div class="content-textarea-wrapper">
          <div
            spellcheck="false"
            class="font-size-middle content-textarea"
            contenteditable="true"
            aria-required="true"
            ref="bandLyricContent"
            data-ph="Tekst piosenki"
            @paste.prevent="handleContentPaste"
            @keyup="handleContentInput($event)"></div>
        </div>
        <CircleLoadingButton v-if="loadingImage" :isLoading="loadingImage" class="image-loader"/>
        <ImageList
            v-if="(imageUploaded || images) && !loadingImage"
            :images="imageList"
            class="image-list font-size-middle"
            @delete="handleImageDelete"
            @moveDown="handleImageMoveDown"
            @moveUp="handleImageMoveUp"
        />
        <ImageUpload v-if="!imageUploaded && !loadingImage"
                    placeholder="Dodaj zdjęcie"
                    @input="handleImageUploaded"
                    @contentExtracted="handleContentExtracted" />
      </section>
      <section class="tablet-preview">
        <TabletPreview :bandLyric="bandLyric" :imageList="imageList"/>
      </section>
    </div>
    <ReplaceLyricContentPopup v-if="isReplaceLyricContentPopup"
                      @cancel="isReplaceLyricContentPopup = false"
                      @replace="replaceLyricContent"/>
    <DeleteLyricPopup v-if="isDeleteLyricPopup"
                      @cancel="cancelLyricDelete"
                      @delete="deleteLyric"/>
    <CannotDeleteLyricPopup v-if="cannotRemoveLyric"
                            :playlistItems="bandLyric.playlistItems"
                            @cancel="cancelLyricDelete"/>
    <AskForSaveLyricPopup v-if="askPopup"
                          @accept="handleSaveClicked(directionOfChangePopup)"
                          @undo="handleLyricChangePage(directionOfChangePopup,'changed')"/>
    <div v-if="isDeleteLyricPopup || cannotRemoveLyric || askPopup"
         class="hide-background"></div>
  </div>
</template>

<script>
import { uniqueId } from 'lodash'
import DefaultInputField from '@/components/input/DefaultInputField.vue'
import SmallButton from '@/components/input/SmallButton.vue'
import ImageList from '@/components/input/ImageList.vue'
import ImageUpload from '@/components/input/ImageUpload.vue'
import TabletPreview from '@/components/TabletPreview.vue'
import DeleteLyricPopup from '@/components/DeleteLyricPopup'
import ReplaceLyricContentPopup from '@/components/ReplaceLyricContentPopup'
import { getBandLyricsService } from '@/services/bandLyricsService'
import { getLyricsService } from '@/services/lyricsService'
import { getAccountService } from '@/services/accountService'
import { getAuthService } from '@/services/authService'
import CannotDeleteLyricPopup from '@/components/CannotDeleteLyricPopup'
import CircleLoadingButton from '@/components/input/CircleLoadingButton'
import AskForSaveLyricPopup from '@/components/AskForSaveLyricPopup.vue'
import MD5 from '@/libraries/MD5/md5-script.js'
import Cleave from 'cleave.js'
import VoerroTagsInput from '@/components/lyrics/VoerroTagsInput.vue'
import { ResizeImage } from '@/services/imageService'

export default {
  name: 'BandLyricForm',
  components: {
    CircleLoadingButton,
    CannotDeleteLyricPopup,
    DeleteLyricPopup,
    ReplaceLyricContentPopup,
    DefaultInputField,
    SmallButton,
    ImageList,
    ImageUpload,
    TabletPreview,
    AskForSaveLyricPopup,
    VoerroTagsInput,
  },

  props: {
    isAndroidContext: {
      type: Boolean,
      default: false,
    },
    isEditingMode: {
      type: Boolean,
      default: false,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    bandLyric: {
      required: true,
    },
    allLyrics: {
      required: false,
    },
    loadingNewPage: {
      type: Boolean,
      required: false
    },
    askPopup: {
      type: Boolean,
      required: false,
    },
    loadingImage: {
      type: Boolean,
      default: false,
    }
  },

  data () {
    return {
      imageList: [],
      isDeleteLyricPopup: false,
      isReplaceLyricContentPopup: false,
      cannotRemoveLyric: false,
      images: null,
      deletedImageUrl: [],
      loadingError: false,
      animate: false,
      isLoading: false,
      imageUploaded: false,
      titleHash: '',
      contentHash: '',
      timeHash: '',
      imagesIsDirty: false,
      contentTitleAreDirty: false,
      directionOfChangePopup: 0,
      time: null,
      selectedTags: [],
      teamTags: [],
      focusInputTags: false,
      loading: false,
      idTags: [],
      contentToReplace: '',
      parsedContent: ''
    }
  },

  directives: {
    cleave: {
      inserted: (el, binding) => {
        el.cleave = new Cleave(el, binding.value || {})
      },
    }
  },

  mounted () {
    (async () => {
      if(!this.bandLyric.band) {
        const band = await getAccountService().getAccount(
        await getAuthService().getLoggedInKey(), false)
        const bandId = band.groupKey;
        this.bandLyric.band = bandId;

        this.bandLyric.lyricTags = []
        
      }
      this.getTags()

      this.time = this.bandLyric.duration.substring(14, 19)
      this.$watch('bandLyric.id', () => {
        if (!this.bandLyric) {
          return
        }

        this.imageList = null
        this.initImageList()
        this.time = this.bandLyric.duration.substring(14, 19)


        if (!this.contentTitleAreDirty) {
          this.titleHash = MD5(this.bandLyric.title)
          this.contentHash = MD5(this.bandLyric.content)
          this.timeHash = MD5(this.bandLyric.duration.substring(14, 19))
        }

        this.parseContent()
      })
      this.initImageList()
      this.titleHash = MD5(this.bandLyric.title)
      this.contentHash = MD5(this.bandLyric.content)
      this.timeHash = MD5(this.bandLyric.duration.substring(14, 19))

      this.parseContent()
    })()
  },

  watch: {
    'bandLyric.id' (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.getTags()
        this.imageUploaded = false
        this.imageList = null
        this.imagesIsDirty = false
        this.deletedImageUrl = []
      }
    }
  },

  computed: {
    contentClass () {
      return {
        saving: this.saving,
        androidContent: this.isAndroidContext,
      }
    },
    pagination () {
      return {
        'button-wrapper': this.pageTitle === 'Edytujesz piosenkę',
        'button-wrapper-empty': this.pageTitle === 'Dodajesz piosenkę'
      }
    },
    header () {
      return {
        'add-new-song': this.pageTitle === 'Dodajesz piosenkę'
      }
    },
    tagsBar () {
      return {
        'dark-bar': !this.selectedTags.length && !this.focusInputTags
      }
    }
  },

  methods: {
    handleContentPaste(e) {
      const text = (e.originalEvent || e).clipboardData.getData('text/plain');
      document.execCommand("insertHTML", false, text);
    },

    newLineToBr(text) {
      return String(text).replace(/\n/g, '<br>')
    },

    htmlToText(text) {
      return text.replace(/<code[^>]*>/g, '[').replace(/<\/code[^>]*>/g, ']').replace(/<br[^>]*>/g, '\n')
    },

    createChordTag(chord) {
      let element = document.createElement('span')
      element.classList.add('chord')
      element.setAttribute('contenteditable', false)

      return `<code contenteditable="false" onclick="this.parentNode.removeChild(this)" class="chord">${chord}</code>`
    },

    parseContent() {
      this.$refs.bandLyricContent.innerHTML = this.newLineToBr(this.bandLyric.content)
      
      let lines = Array.from(this.$refs.bandLyricContent.childNodes).filter(key => key.nodeName === '#text')

      for(let i = 0; i < lines.length; i++) {
        this.parseTag(lines[i])
      }

      this.updateBandLyricContent()
    },

    parseTag(line) {
      let selection = document.getSelection(),
          range = document.createRange(),
          chord = line.textContent.match(/\[\S{1,2}\]/)

      if(chord !== null) {
          let currentRange = document.createRange(),
              chordSign = chord[0].match(/(?<=\[).*(?=\])/)

          currentRange.setStart(line, chord.index + chord[0].length)

          range.setStart(line, chord.index)
          range.setEnd(line, chord.index + chord[0].length)

          selection.removeAllRanges()
          selection.addRange(range)
          
          document.execCommand('insertHTML', false, this.createChordTag(chordSign))
          
          selection.removeAllRanges()
          selection.addRange(currentRange)

          if(line.textContent.match(/\[\S{1,2}\]/)) {
            this.parseTag(line)
          }
      }
    },

    updateBandLyricContent() {
      this.bandLyric.content = this.$refs.bandLyricContent.innerHTML.replace(/<code[^>]*>(.*?)<\/code>/g, '')
                                                                    .replace(/<br>/g, '\n')
    },

    handleContentInput() {
      let line = document.getSelection().focusNode

      this.parseTag(line)
      this.updateBandLyricContent()
    },

    handleContentExtracted(content) {
      this.contentToReplace = content[0]
      if(this.bandLyric.content.length > 0) {
        this.isReplaceLyricContentPopup = true
      } else {
        this.replaceLyricContent()
      }
    },

    replaceLyricContent() {
      this.bandLyric.content = this.contentToReplace
      this.$refs.bandLyricContent.innerHTML = this.newLineToBr(this.contentToReplace)
      this.isReplaceLyricContentPopup = false
    },

    async addNewTag () {
      this.$emit('setLoading')
      const existedTags = this.teamTags.map(tag => tag.value)
      const saveStatusTags = this.selectedTags.map(tag => tag.value)
      let needToSaveTags = []

      for (const exT of saveStatusTags) {
        if (!existedTags.includes(exT)) {
          needToSaveTags.push(exT)
        }
      }

      this.idTags = []

      this.selectedTags.forEach(item => {
        if (item.id) {
          this.idTags.push(`/api/lyric_tags/${item.id}`)
        }
      })

      if (needToSaveTags.length > 0) {
        const payload = []

        needToSaveTags.forEach(item => {
          let arrayTag = {
            name: item,
            bandLyric: [this.bandLyric['@id']]
          }
          payload.push(arrayTag)
        })

        for (const item of payload) {
          await getBandLyricsService().createBandLyricTags(item).then(resp =>
          this.idTags.push(`/api/lyric_tags/${resp.data.id}`))
        }
      }
      },

    async getTags () {
      const bandId = this.bandLyric.band.split("/")[3]

      this.selectedTags = this.bandLyric.lyricTags.map(({name, ...rest}) => ({value: name, key: name, ...rest}))

      const tags = await getBandLyricsService().getBandLyricTags(bandId)

      if(tags.length !== 0) {
        this.teamTags = tags.map(({name, ...rest}) => ({key: name, value: name, ...rest}))
      }
    },

    handleBarColor() {
      this.focusInputTags === true ? this.focusInputTags = false : this.focusInputTags = true
    },

    cancelLyricDelete () {
      this.isDeleteLyricPopup = false
      this.cannotRemoveLyric = false
    },

    cancelReplaceLyricPopup () {
      this.isReplaceLyricContentPopup = false
    },

    confirmLyricDelete () {
      this.isDeleteLyricPopup = true
    },

    async deleteLyric () {
      for(let i = 0, items = this.bandLyric.playlistItems; i < items.length; i++) {
        let id = items[i].substr(20)
        await getLyricsService().deletePlaylistItem(id)
      }

      if (this.images.length !== 0) {
        await getBandLyricsService().deleteBandLyricPictures(this.$route.params.id)
      }
      await getBandLyricsService().deleteBandLyric(this.$route.params.id)
      await this.$router.push({ name: 'lyrics-panel' })
    },

    cancelBandLyricForm () {
      this.$router.push({ name: 'lyrics-panel' })
    },

    async initImageList () {
      if (this.images != null && this.imageList != null) {
        return
      }
      this.images = await getBandLyricsService().getBandLyricPictures(this.$route.params.id)

      if (this.images[0]?.bandLyric.slice(17) == this.$route.params.id) {
        this.imageList = this.images.map((image) => ({
          key: uniqueId('image-'),
          image: image.url,
        }))
      }

      if (this.images.length !== 0) {
        this.imageUploaded = true
        this.$emit('loadImage', false)
      } else {
        this.imageUploaded = false
        this.$emit('loadImage', false)
      }
    },

    offsetImage (imageKey, offset) {
      const element = this.imageList.find((i) => i.key == imageKey)
      const oldIndex = this.imageList.indexOf(element)
      const newIndex = Math.min(
          Math.max(0, oldIndex + offset),
          this.imageList.length - 1
      )

      const newImageList = this.imageList.slice()
      newImageList.splice(oldIndex, 1)
      newImageList.splice(newIndex, 0, element)
      this.imageList = newImageList
    },

    handleTimeChange () {
      this.contentTitleAreDirty = true
      if (this.time.length>5) {
        this.time = this.time.substring(0, this.time.length-1)
      }
      let duration = this.time
      this.$emit('change', { duration })
    },

    handleTitleChange (title) {
      this.contentTitleAreDirty = true
      this.$emit('change', { title })
    },

    handleContentChange (content) {
      this.contentTitleAreDirty = true
      this.$emit('change', { content })
    },

    async handleImageUploaded (image) {
      this.imagesIsDirty = true
      this.$emit('loadImage', true)

      this.imageList = []

      this.imageList.push({
        key: uniqueId('image-'),
        image: image,
      })
      this.imageUploaded = true
      this.$emit('loadImage', false)
    },

    async handleSaveClicked (directionOfChangePopup) {      
      if (this.bandLyric.title.length !== 0 && this.time.length !== 0) {
        this.loadingError = false
        this.animate = false
        if (typeof directionOfChangePopup !== "number") {
          this.isLoading = true
        }
        for (const url of this.deletedImageUrl) {
          for (const image of this.images) {
            if (url === image.url) {
              await getBandLyricsService().deleteBandLyricPicture(image.id)
            }
          }
        }
        if(this.bandLyric['@id']) {
          await this.addNewTag()
          this.bandLyric.duration = `00:${this.time.slice(0,5)}`
          this.bandLyric.lyricTags = this.idTags
        }

        this.bandLyric.content = this.htmlToText(this.$refs.bandLyricContent.innerHTML)
        this.$emit('save', {
            ...this.bandLyric,
        }, directionOfChangePopup, this.selectedTags)
        if (this.imageList) {
          this.bandLyric.images = this.imageList.map((row) => row.image)
        } else {
          this.bandLyric.images = []
        }
      } else {
        this.loadingError = true
        this.animate = true
        this.isLoading = false
        this.$emit('handleAskPopup', false)
      }
    },

    handleImageMoveUp (imageKey) {
      this.offsetImage(imageKey, -1)
    },

    handleImageMoveDown (imageKey) {
      this.offsetImage(imageKey, 1)
    },

    async handleImageDelete (imageKey) {
      this.imagesIsDirty = true
      for (const image of this.imageList) {
        if (imageKey === image.key) {
          this.deletedImageUrl.push(image.image)
        }
      }

      this.imageList = this.imageList.filter(
          (row) => row.key != imageKey
      )
      this.imageUploaded = false
    },

    handleLyricChangePage (direction, changed) {
      if (changed) {
        this.$emit('handleAskPopup', false)
        this.imagesIsDirty = false
        this.$emit('changeLyricPage', direction)
        this.images = null
        return
      }

      const initialTags = this.bandLyric.lyricTags.map(item => item.name)
      const currentTags = this.selectedTags.map(item => item.value)

      let areTagsDirty = !_.isEqual(initialTags, currentTags)

      const currentTitleHash = MD5(this.bandLyric.title)
      const currentContentHash = MD5(this.htmlToText(this.$refs.bandLyricContent.innerHTML))
      const currentTimeHash = MD5(this.bandLyric.duration.substring(14, 19))
      this.contentTitleAreDirty = false

      this.loadingError = false
      this.animate = false
      if (currentTitleHash !== this.titleHash ||
          currentContentHash !== this.contentHash ||
          currentTimeHash !== this.timeHash ||
          this.imagesIsDirty ||
          this.time !== this.bandLyric.duration.substring(14,19) ||
          areTagsDirty) {
        this.directionOfChangePopup = direction
        this.$emit('handleAskPopup', true)
      } else {
        this.$emit('changeLyricPage', direction)
        this.$emit('loadImage', true)
      }
    }
  },
}
</script>

<style lang="scss">
.chord {
  color: red;
  position: relative;
  width: 0;
  display: inline-flex;
  top: -1rem;
  user-select: none;
  cursor: pointer;
}
</style>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';

.hide-background {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background-color: #1a1a1a;
  opacity: 0.5;
}

.content {
  max-width: 900px;
  margin: 91px auto 0;
  padding: 0 10px;

  &.saving {
    pointer-events: none;
  }
}

.header-wrapper {
  display: grid;
  grid-template-columns: 200px 1fr 200px;
  grid-template-rows: 1fr;
  padding: 0 5px;
  border-bottom: 1px solid #aaa;

  @media screen and (max-width: $phone-max-width) {
    grid-template-columns: 85px 1fr 85px;
  }

  &.add-new-song {
    grid-template-columns: 1fr;
  }

  header {
    width: auto;
    margin: 0 auto;
  }

  .button-change-page {
    height: min-content;
    max-width: 100px;
    align-self: flex-start;
    border-color: #d6ae5d;

    @media screen and (max-width: $phone-max-width) {
      min-width: auto;
      width: min-content;
      justify-self: self-start;
      align-self: center;
    }
  }

  .button-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;

    @media screen and (max-width: $phone-max-width) {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr;
    }

    p {
      margin: 0;
      color: #777;
    }
  }

  .button-wrapper-empty {
    display: none;
  }

  .button-wrapper.next {
    grid-column: 3;
    .button-change-page {
      align-self: flex-end;

      @media screen and (max-width: $phone-max-width) {
        min-width: auto;
        width: min-content;
        justify-self: self-end;
        align-self: center;
      }
    }

    p {
      text-align: end;
    }


    @media screen and (max-width: $phone-max-width) {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr;
    }
  }

}

header {
  flex-grow: 1;

  h1 {
    color: $primary;
    text-align: center;
    margin-top: 0;

    @media screen and (max-width: $phone-max-width) {
      padding-top: 15px;
      margin-bottom: 20px;
    }
  }
}

.actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  border-radius: 6px;

  & > * {
    margin-right: 10px;
    @media only screen and (max-width: 350px) {
      min-width: 80px;
    }
  }

}

.androidContent {
  margin-top: 30px;
}

.own-lyric-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;

  .input-fields {
    margin-right: 50px;
    margin-top: 40px;

    &.error {
      margin-top: 0;
    }

    .upper-inputs {
      display: grid;
      grid-template-columns: 1fr 100px;

      .time-input {
        margin-left: 20px;
      }

      .text-input {
        position: relative;
        display: block;
        height: 56px;
        width: 90%;
      }

      .text-input > input {
        position: absolute;
        bottom: 0;
        display: block;
        height: 40px;
        width: 90%;
        line-height: 40px;
        border: 0;
        border-bottom: 1px solid $grey-light;
        border-radius: 0;
        outline: 0;
        background: transparent;
        color: $grey-light;
      }

      .text-input > input::placeholder {
        color: transparent;
      }

      .text-input > label {
        position: absolute;
        top: 25px;
        left: 24px;
        color: $grey-light;
        cursor: text;
        transition: all 0.2s ease-in-out;
        user-select: none;
        text-overflow: ellipsis;
        overflow: hidden;
        width: 90%;
        white-space: nowrap;
        z-index: -1;
      }

      .text-input > input:focus,
      .text-input > input:not(:placeholder-shown) {
        border-bottom: 2px solid $primary;
      }

      .text-input > input:focus + label,
      .text-input > input:not(:placeholder-shown) + label {
        top: 0;
        left: 22px;
        font-size: 14px;
        color: $primary;
      }

      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-transition-delay: 9999s;
        -webkit-transition: color 9999s ease-out, background-color 9999s ease-out;
      }

      &.invalid {
        .text-input > input {
          border-bottom-color: $red;
        }

        .text-input > input + label {
          color: $red;
        }
      }
    }

    .tags-wrapper {
      width: inherit;
      padding-bottom: 10px;
      color: black;
      border-bottom: 2px solid $primary;

      .tags-input-remove {
        margin-left: 5px;
        color: #9d0000;
      }
    }

    .dark-bar {
      border-color: grey;
      width: inherit;
    }

    .textarea-wrapper {
      margin-top: 20px;
    }

    .image-list {
      margin-top: 10px;
    }

    .lyricError {
      height: 40px;
      font-weight: normal;
      display: flex;
      width: 100%;
      justify-content: center;
      color: $red;

      div {
        display: flex;
      }

      p {
        margin-left: 15px;
      }

      img {
        max-width: 1.5rem;
        fill: $red;
      }

      .bounce {
        animation-name: bounce;
        animation-timing-function: linear;
        animation-duration: 1.25s;
        animation-iteration-count: 2;
        transition: all 0.25s;
      }

      @keyframes bounce {
        0% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-3px);
        }
        100% {
          transform: translateY(0);
        }
      }
    }

    .image-loader {
      margin-top: 20px;
    }
  }

  .tablet-preview {
    display: flex;
    justify-content: space-between;
  }
}

// Mobile style
@media (max-width: $phone-max-width) {
  .own-lyric-form {
    grid-template-columns: 1fr;

    .input-fields {
      display: flex;
      flex-wrap: wrap;
      flex: 1 1;
      width: 90vw;
      margin-left: auto;
      margin-right: auto;

      .upper-inputs {
        grid-template-columns: 1fr 80px;
        width: 90vw;
      }

      .textarea-wrapper {
        width: 90vw;
        justify-content: center;
        margin-top: 30px;
      }

      .image-loader {
        margin: 20px auto 20px;
      }
    }
  }
}

.content-textarea-wrapper {
  width: 100%;
  .content-textarea:focus,
  .content-textarea:not(:placeholder-shown) {
    border-bottom: 2px solid $primary;
  }

  .content-textarea:focus + label,
  .content-textarea:not(:placeholder-shown) + label {
    top: 0;
    left: 0;
    font-size: 14px;
    color: $primary;
  }

  .textarea-group {
    position: relative;
    padding: 15px 0 0;
    margin-top: 10px;
  }

  .content-textarea {
    font-family: inherit;
    width: 100%;
    border: 0;
    border-bottom: 1px solid $grey-light;
    outline: 0;
    color: $grey-light;
    padding: 7px 0;
    background: transparent;
    transition: border-color 0.2s;
    height: 26em;
    overflow-y: scroll;
    // white-space: pre-wrap;
    line-height: 2.2rem;
    display: inline-block;
  }

  .content-textarea:placeholder-shown ~ .textarea-label {
    cursor: text;
    top: 25px;
    left: 9px;
  }

  label,
  .textarea-field:focus ~ .textarea-label {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    transition: all 0.2s ease-in-out;
    color: $grey-light;
  }

  .textarea-field:focus ~ .textarea-label {
    color: $primary;
  }

  .textarea-field:focus {
    padding-bottom: 6px;
    border-bottom: 2px solid $primary;
  }
}

[contenteditable=true]:empty:not(:focus):before{
  content:attr(data-ph);
  color:grey;
}

/* width */
.content-textarea::-webkit-scrollbar {
  width: 10px;
}

/* Track */
.content-textarea::-webkit-scrollbar-track {
  background: $grey-light;
  border: thin solid $grey-light;
  border-radius: 10px;
}

/* Handle */
.content-textarea::-webkit-scrollbar-thumb {
  background: $dark-text;
  border: thin solid $dark-text;
  border-radius: 10px;
}

/* Handle on hover */
.content-textarea::-webkit-scrollbar-thumb:hover {
  background: $primary;
  border: thin solid $primary;
  border-radius: 10px;
}
</style>
